<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { nodeOverview, NodeSnapshot } from '@shell/utils/lattice-node-overview';
import NodePoolMembership from '@shell/components/lattice/NodePoolMembership.vue';
import { formatSi } from '@shell/utils/units';

export default defineComponent({
  components: { NodePoolMembership },
  props:      {
    selectedRole: { type: String, default: '' },
    selectedZone: { type: String, default: '' },
    roleChoices:  { type: Array as PropType<string[]>, default: () => [] },
    zoneChoices:  { type: Array as PropType<string[]>, default: () => [] },
    clusterId:    { type: String, default: '' },
    clusterName:  { type: String, default: '' },
    nodes:        { type: Array as PropType<NodeSnapshot[]>, default: () => [] },
    paginated:    { type: Boolean, default: false },
    partialScope: { type: Boolean, default: false }
  },
  emits:    ['toggle-role', 'toggle-zone', 'clear-filters'],
  computed: {
    roles() {
      return Array.from(new Set([...this.overview.roles.map((entry) => entry.label), ...this.roleChoices, this.selectedRole].filter(Boolean))).map((label) => ({ label, count: this.overview.roles.find((entry) => entry.label === label)?.count }));
    },
    zones() {
      return Array.from(new Set([...this.overview.zones.map((entry) => entry.label), ...this.zoneChoices, this.selectedZone].filter(Boolean))).map((label) => ({ label, count: this.overview.zones.find((entry) => entry.label === label)?.count }));
    },
    overview() {
      return nodeOverview(this.nodes);
    },
    cpuText(): string {
      return this.overview.cpuReported ? `${ Number(this.overview.cpu.toFixed(3)) } cores` : 'Unavailable';
    },
    memoryText(): string {
      return this.overview.memoryReported ? formatSi(this.overview.memory, {
        increment: 1024, suffix: 'iB', firstSuffix: 'B', maxPrecision: 1
      }) : 'Unavailable';
    },
    scopeText(): string {
      return this.paginated || this.partialScope ? 'Loaded node snapshot · may include only part of the cluster; separate from table filters.' : 'Loaded node snapshot · separate from table filters.';
    },
    readinessText(): string {
      return `${ this.overview.notReady } not ready${ this.overview.unknown ? ` · ${ this.overview.unknown } unknown` : '' }`;
    },
  },
});
</script>
<template>
  <section
    class="node-overview"
    aria-label="Loaded node infrastructure overview"
  >
    <div class="overview-heading">
      <strong>Infrastructure snapshot</strong><span>{{ scopeText }}</span>
    </div>
    <div class="overview-metrics">
      <div class="metric">
        <span class="metric-label">Loaded nodes</span><strong>{{ overview.total }}</strong><span class="metric-note">{{ paginated || partialScope ? 'Partial cluster inventory' : 'Available in this view' }}</span>
      </div>
      <div class="metric">
        <span class="metric-label">Ready</span><strong>{{ overview.ready }}<span class="metric-total"> / {{ overview.total }}</span></strong><span
          class="metric-note"
          :class="{ attention: overview.notReady > 0 }"
        >{{ readinessText }}</span>
      </div>
      <div class="metric">
        <span class="metric-label">Cordoned</span><strong>{{ overview.cordoned }}</strong><span class="metric-note">Scheduling disabled</span>
      </div>
      <div class="metric">
        <span class="metric-label">Reported allocatable CPU</span><strong class="capacity">{{ cpuText }}</strong><span class="metric-note">{{ overview.cpuReported }} of {{ overview.total }} nodes report capacity</span>
      </div>
      <div class="metric">
        <span class="metric-label">Reported allocatable memory</span><strong class="capacity">{{ memoryText }}</strong><span class="metric-note">{{ overview.memoryReported }} of {{ overview.total }} nodes report capacity</span>
      </div>
    </div>
    <div
      v-if="overview.total || selectedRole || selectedZone || roleChoices.length || zoneChoices.length"
      class="distribution-row"
    >
      <div class="distribution">
        <span class="distribution-label">Role labels</span>
        <template
          v-for="role in roles"
          :key="role.label"
        >
          <span
            v-if="paginated && role.label === 'Role unspecified'"
            class="distribution-chip"
            title="No role label in the loaded snapshot; unavailable as a server filter"
          >
            {{ role.label }} <b v-if="role.count !== undefined">{{ role.count }}</b>
          </span>
          <button
            v-else
            type="button"
            class="distribution-chip"
            :aria-pressed="selectedRole === role.label"
            :title="`Filter inventory by role: ${role.label}`"
            @click="$emit('toggle-role', role.label)"
          >
            {{ role.label }} <b v-if="role.count !== undefined">{{ role.count }}</b>
          </button>
        </template>
      </div>
      <div
        v-if="zones.length"
        class="distribution"
      >
        <span class="distribution-label">Zone labels</span><button
          v-for="zone in zones"
          :key="zone.label"
          type="button"
          :aria-pressed="selectedZone === zone.label"
          class="distribution-chip"
          :title="`Filter inventory by zone: ${zone.label}`"
          @click="$emit('toggle-zone', zone.label)"
        >
          {{ zone.label }} <b v-if="zone.count !== undefined">{{ zone.count }}</b>
        </button><span
          v-if="overview.zoneReported < overview.total"
          class="unknown-zones"
        >{{ overview.total - overview.zoneReported }} unlabelled</span>
      </div>
      <button
        v-if="selectedRole || selectedZone"
        type="button"
        class="clear-filters"
        @click="$emit('clear-filters')"
      >
        Clear inventory filters
      </button>
    </div>
    <NodePoolMembership
      :nodes="nodes"
      :cluster-id="clusterId"
      :cluster-name="clusterName"
    />
  </section>
</template>
<style lang="scss" scoped>
.node-overview { border: 1px solid var(--dev-border, var(--border)); border-radius: 8px; background: var(--dev-surface, var(--body-bg)); color: var(--dev-ink, var(--body-text)); margin-bottom: 20px; }
.overview-heading { padding: 12px 16px 0; display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px 14px; strong { font-size: 13px; font-weight: 500; } span { color: var(--dev-muted, var(--muted)); font-size: 12px; line-height: 1.4; } }
.overview-metrics { display: grid; grid-template-columns: repeat(3,minmax(0,.75fr)) repeat(2,minmax(0,1.25fr)); padding: 16px 0; }
.metric { display: flex; flex-direction: column; gap: 8px; padding: 0 16px; border-right: 1px solid var(--dev-border, var(--border)); min-width: 0; &:last-child { border-right: 0; } > strong { font-size: 24px; font-weight: 600; line-height: 1.2; } > .capacity { font-size: 20px; overflow-wrap: anywhere; } }
.metric-label { font-size: 12px; color: var(--dev-muted, var(--muted)); }.metric-total { font-size: 14px; color: var(--dev-muted, var(--muted)); font-weight: 400; }.metric-note { font-size: 12px; color: var(--dev-muted, var(--muted)); line-height: 1.4; &.attention { color: var(--warning); } }
.distribution-row { border-top: 1px solid var(--dev-border, var(--border)); display: flex; flex-wrap: wrap; gap: 10px 28px; padding: 10px 16px; }.distribution { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; min-width: 0; font-size: 12px; }.distribution-label { color: var(--dev-muted, var(--muted)); margin-right: 4px; }.distribution-chip { display: inline-flex; gap: 8px; align-items: center; padding: 3px 7px; border: 1px solid var(--dev-border, var(--border)); border-radius: 4px; overflow-wrap: anywhere; b { font-weight: 500; color: var(--dev-muted, var(--muted)); } }.unknown-zones { color: var(--dev-muted, var(--muted)); }
button.distribution-chip { color: inherit; background: transparent; font: inherit; cursor: pointer; &[aria-pressed="true"] { border-color: var(--primary); background: var(--primary-banner-bg); } &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; } }
.clear-filters { font-size: 12px; color: var(--link); background: transparent; border: 0; cursor: pointer; padding: 4px; }
@media(max-width: 1050px) { .overview-metrics { grid-template-columns: repeat(3,minmax(0,1fr)); gap: 18px 0; }.metric:nth-child(3) { border-right: 0; }.metric:nth-child(4) { grid-column: 1 / 3; } }
@media(max-width: 600px) { .overview-metrics { grid-template-columns: repeat(2,minmax(0,1fr)); }.metric:nth-child(2n) { border-right: 0; }.metric:nth-child(3) { border-right: 1px solid var(--dev-border); }.metric:nth-child(4) { grid-column: auto; }.metric:last-child { grid-column: 1 / -1; }.metric > strong { font-size: 22px; }.metric > .capacity { font-size: 18px; } }
</style>
